/**
 * Edit User Profile Dialog (CR-5857)
 *
 * Lets an admin edit a managed user's identity / contact fields and role
 * without needing the user's session. Submits only the fields that changed
 * via coreAPI.updateUserProfileFields (wired through useUserActions).
 */

import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Loader2, Save } from 'lucide-react';
import { UserProfile, Role } from '../../lib/api';
import type { UpdateUserProfileFields } from './useUserActions';

interface EditUserProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: UserProfile | null;
  availableRoles: Role[];
  actionLoading: boolean;
  onSave: (userProfile: UserProfile, data: UpdateUserProfileFields) => Promise<boolean>;
}

const NO_ROLE = 'none';

export function EditUserProfileDialog({
  open,
  onOpenChange,
  selectedUser,
  availableRoles,
  actionLoading,
  onSave,
}: EditUserProfileDialogProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [roleId, setRoleId] = useState<string>(NO_ROLE);

  // Re-seed the form whenever a new user is selected / dialog re-opens.
  useEffect(() => {
    if (selectedUser && open) {
      setFirstName(selectedUser.user.first_name || '');
      setLastName(selectedUser.user.last_name || '');
      setEmail(selectedUser.user.email || '');
      setPhone(selectedUser.phone || '');
      setJobTitle(selectedUser.job_title || '');
      setDepartment(selectedUser.department || '');
      setRoleId(selectedUser.role ? String(selectedUser.role) : NO_ROLE);
    }
  }, [selectedUser, open]);

  if (!selectedUser) return null;

  // Only offer roles that match the target user's company type (mirrors the
  // backend company_type<->role gating).
  const compatibleRoles = availableRoles.filter(
    (r) => r.company_type === selectedUser.company_type
  );

  const handleSubmit = async () => {
    const data: UpdateUserProfileFields = {};
    if (firstName.trim() !== (selectedUser.user.first_name || '')) data.first_name = firstName.trim();
    if (lastName.trim() !== (selectedUser.user.last_name || '')) data.last_name = lastName.trim();
    if (email.trim().toLowerCase() !== (selectedUser.user.email || '').toLowerCase()) data.email = email.trim();
    if (phone.trim() !== (selectedUser.phone || '')) data.phone = phone.trim();
    if (jobTitle.trim() !== (selectedUser.job_title || '')) data.job_title = jobTitle.trim();
    if (department.trim() !== (selectedUser.department || '')) data.department = department.trim();
    const currentRole = selectedUser.role ? String(selectedUser.role) : NO_ROLE;
    if (roleId !== currentRole) {
      data.role = roleId === NO_ROLE ? null : Number(roleId);
    }
    await onSave(selectedUser, data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
          <DialogDescription>
            Update details for {selectedUser.user.full_name}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-first-name">First name</Label>
              <Input
                id="edit-first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-last-name">Last name</Label>
              <Input
                id="edit-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-email">Email</Label>
            <Input
              id="edit-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-phone">Telephone</Label>
              <Input
                id="edit-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-job-title">Position</Label>
              <Input
                id="edit-job-title"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-department">Department</Label>
            <Input
              id="edit-department"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-role">Role</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger id="edit-role">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_ROLE}>No role</SelectItem>
                {compatibleRoles.map((r) => (
                  <SelectItem key={r.id} value={String(r.id)}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={actionLoading}>
            {actionLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
